import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, Download, Loader2, Upload, X } from "lucide-react";
import { ApiClientError } from "@pushup/shared";
import { useCreatePublicVideo, type PublicVideoUpload } from "@/features/public-videos";
import { authClient } from "@/lib/auth";
import type { ClipRecordingResult } from "../hooks/use-clip-recorder";
import { ShareButton } from "./share-button";

/** Max size for a clip shared/published from a session. */
const MAX_SHARE_BYTES = 100 * 1024 * 1024;

/** Shared structure for the download / publish / share action row. */
const actionBase =
  "flex h-12 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-40";
const actionSurface = "border border-white/15 bg-white/5 text-white hover:bg-white/10";

interface RecordedClipOverlayProps {
  clip: ClipRecordingResult;
  reps: number;
  celebrityName?: string | null;
  onFinish: () => void;
}

/**
 * Full-screen replay of the clip recorded during a session, shown when the
 * user stops the recording (which ends the session). Three actions over the
 * video: download the file, publish it to the public feed, or share it via the
 * native share sheet.
 */
export function RecordedClipOverlay({
  clip,
  reps,
  celebrityName,
  onFinish,
}: RecordedClipOverlayProps) {
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const createClip = useCreatePublicVideo();
  const { data: session } = authClient.useSession();
  const signedIn = Boolean(session?.session);

  // Object URL is created/revoked inside the effect so StrictMode's dev
  // double-invoke keeps a valid URL alive (revoking a URL created by useMemo
  // breaks the video src in development).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(clip.blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [clip]);

  const tooLargeToPost = clip.blob.size > MAX_SHARE_BYTES;

  const handleDownload = () => {
    if (!previewUrl) return;
    const link = document.createElement("a");
    link.href = previewUrl;
    link.download = "pushup-clip.webm";
    link.click();
  };

  const handlePublish = async () => {
    setPostError(null);
    setPosting(true);
    try {
      const input: PublicVideoUpload = {
        name: "Pushup clip",
        mimeType: clip.mimeType,
        blob: clip.blob,
        durationSec: clip.durationSec,
        reps,
        celebrityName: celebrityName ?? null,
      };
      await createClip.mutateAsync(input);
      setPosted(true);
    } catch (err) {
      setPostError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't publish the clip. Try again.",
      );
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex flex-col bg-black">
      <div className="flex items-center justify-between px-5 py-4">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-white/70">Your clip</h2>
        <button
          type="button"
          onClick={onFinish}
          aria-label="Close"
          className="rounded-full p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="size-6" />
        </button>
      </div>

      {previewUrl && (
        <video
          src={previewUrl}
          autoPlay
          loop
          muted
          playsInline
          className="min-h-0 w-full flex-1 object-contain"
        />
      )}

      <div className="space-y-3 px-5 pb-8 pt-5">
        {postError && (
          <p className="rounded-lg border border-red-500/30 bg-red-950/50 px-3 py-2 text-xs text-red-200">
            {postError}
          </p>
        )}
        {tooLargeToPost && (
          <p className="rounded-lg border border-amber-500/30 bg-amber-950/40 px-3 py-2 text-xs text-amber-200">
            This clip is over 100 MB — download or share it instead of publishing.
          </p>
        )}

        <div className="grid grid-cols-3 gap-2.5">
          <button
            type="button"
            onClick={handleDownload}
            className={`${actionBase} ${actionSurface}`}
          >
            <Download className="size-4" />
            Download
          </button>
          {signedIn ? (
            <button
              type="button"
              onClick={() => void handlePublish()}
              disabled={tooLargeToPost || posting || posted}
              className={`${actionBase} ${
                posted
                  ? "bg-emerald-500 text-white shadow-[0_8px_24px_rgba(16,185,129,0.35)] hover:bg-emerald-400"
                  : "bg-[#00a2ff] text-white shadow-[0_8px_24px_rgba(0,162,255,0.35)] hover:bg-[#1badff]"
              }`}
            >
              {posting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : posted ? (
                <Check className="size-4" />
              ) : (
                <Upload className="size-4" />
              )}
              {posting ? "Publishing…" : posted ? "Published" : "Publish"}
            </button>
          ) : (
            <Link
              to="/login"
              className={`${actionBase} border border-[#00a2ff]/40 bg-[#00a2ff]/10 text-[#00a2ff] hover:bg-[#00a2ff]/20`}
            >
              <Upload className="size-4" />
              Publish
            </Link>
          )}
          <ShareButton
            reps={reps}
            celebrityName={celebrityName}
            clip={clip}
            className={actionSurface}
          />
        </div>
      </div>
    </div>
  );
}
